import { useState } from 'react';
import { Sparkles, CheckCircle, XCircle, Loader } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient } from '../lib/api';

function GeminiTestPage() {
    const [testing, setTesting] = useState(false);
    const [result, setResult] = useState(null);

    const testGeminiAPI = async () => {
        setTesting(true);
        setResult(null);

        try {
            const response = await apiClient.testGemini();
            setResult(response);

            if (response.success) {
                toast.success(`✅ Gemini API Works! Model: ${response.workingModel}`);
            } else {
                toast.error('❌ Gemini API Failed');
            }
        } catch (error) {
            const errorData = error.response?.data || { error: error.message };
            setResult(errorData);
            toast.error('❌ Test Failed');
        } finally {
            setTesting(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto p-8">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center">
                    <Sparkles className="w-8 h-8 mr-3 text-purple-600" />
                    Gemini AI API Test
                </h1>
                <p className="text-gray-600">
                    Test your Gemini API key and find which model works
                </p>
            </div>

            {/* Test Button */}
            <div className="card mb-6">
                <button
                    onClick={testGeminiAPI}
                    disabled={testing}
                    className="btn-primary w-full flex items-center justify-center disabled:opacity-50"
                >
                    {testing ? (
                        <>
                            <Loader className="w-5 h-5 mr-2 animate-spin" />
                            Testing...
                        </>
                    ) : (
                        <>
                            <Sparkles className="w-5 h-5 mr-2" />
                            Test Gemini API
                        </>
                    )}
                </button>
            </div>

            {/* Results */}
            {result && (
                <div className="card">
                    <div className="flex items-start mb-4">
                        {result.success ? (
                            <CheckCircle className="w-6 h-6 text-green-600 mr-3 flex-shrink-0" />
                        ) : (
                            <XCircle className="w-6 h-6 text-red-600 mr-3 flex-shrink-0" />
                        )}
                        <div className="flex-1">
                            <h3 className={`text-lg font-semibold mb-2 ${result.success ? 'text-green-700' : 'text-red-700'}`}>
                                {result.success ? 'Success!' : 'Failed'}
                            </h3>

                            {result.success ? (
                                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                    <p className="text-sm text-gray-700 mb-2">
                                        <strong>Working Model:</strong> <code className="bg-green-100 px-2 py-1 rounded">{result.workingModel}</code>
                                    </p>
                                    <p className="text-sm text-gray-700 mb-2">
                                        <strong>Response:</strong>
                                    </p>
                                    <div className="bg-white border rounded p-3 text-sm text-gray-800">
                                        {result.response}
                                    </div>
                                    <p className="text-xs text-green-600 mt-3">
                                        ✅ {result.message}
                                    </p>
                                </div>
                            ) : (
                                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                    <p className="text-sm text-gray-700 mb-2">
                                        <strong>Error:</strong>
                                    </p>
                                    <div className="bg-white border rounded p-3 text-sm text-red-600 mb-3">
                                        {result.error}
                                    </div>

                                    {result.attemptedModels && (
                                        <div className="mb-3">
                                            <p className="text-sm font-semibold text-gray-700 mb-2">Attempted Models:</p>
                                            <div className="space-y-1">
                                                {result.attemptedModels.map((attempt, idx) => (
                                                    <div key={idx} className="flex items-center text-xs">
                                                        {attempt.status === 'SUCCESS' ? (
                                                            <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                                                        ) : (
                                                            <XCircle className="w-4 h-4 text-red-500 mr-2" />
                                                        )}
                                                        <span className="font-mono">{attempt.model}</span>
                                                        <span className="ml-2 text-gray-500">- {attempt.status}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {result.hint && (
                                        <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mt-3">
                                            <p className="text-xs text-yellow-800">
                                                <strong>💡 Hint:</strong> {result.hint}
                                            </p>
                                        </div>
                                    )}

                                    {result.apiKeyProvided !== undefined && (
                                        <div className="mt-3 text-xs text-gray-600">
                                            <p>API Key Provided: {result.apiKeyProvided ? '✅ Yes' : '❌ No'}</p>
                                            {result.apiKeyPrefix && (
                                                <p>API Key Prefix: <code className="bg-gray-100 px-1">{result.apiKeyPrefix}</code></p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Instructions */}
            <div className="card mt-6 bg-blue-50 border-blue-200">
                <h3 className="font-semibold text-blue-900 mb-3">📋 Setup Instructions</h3>
                <ol className="text-sm text-blue-800 space-y-2 list-decimal list-inside">
                    <li>Get your Gemini API key from <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer" className="underline">Google AI Studio</a></li>
                    <li>Add to <code className="bg-blue-100 px-1">backend/.env</code>:</li>
                    <code className="block bg-blue-100 p-2 rounded my-2">GEMINI_API_KEY=your_key_here</code>
                    <li>Restart backend server</li>
                    <li>Click "Test Gemini API" above</li>
                </ol>
            </div>
        </div>
    );
}

export default GeminiTestPage;
